import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Search, Loader2, X, User, FileText, Calendar, Package,
    ShieldCheck, Truck, Warehouse, Receipt, AlertTriangle, Phone, Star, MapPin, Mail, Clock, CheckCircle2, Eye, Trash2, Monitor, CalendarDays, UserCheck, ParkingCircle,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { StatusHistoryDialog } from '../prospects/StatusHistoryDialog';
import { VisiteHistoryDialog } from '../visites/VisiteHistoryDialog';

const API = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Constantes design (copiées des pages d'origine pour garder l'iso-rendu)
// ---------------------------------------------------------------------------
const PROSPECT_STATUS_COLORS = {
    nouveau: 'bg-blue-100 text-blue-700 border-blue-200',
    sans_reponse_1: 'bg-orange-100 text-orange-700 border-orange-200',
    sans_reponse_2: 'bg-red-100 text-red-700 border-red-200',
    sans_reponse_3: 'bg-rose-100 text-rose-700 border-rose-200',
    a_rappeler: 'bg-amber-100 text-amber-700 border-amber-200',
    devis_estimatif: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    listing_en_attente: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    listing_telephone: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    listing_email: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    visite: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    faux_contact: 'bg-slate-200 text-slate-700 border-slate-300',
    non_interessant: 'bg-slate-200 text-slate-700 border-slate-300',
};
const PROSPECT_STATUS_LABELS = {
    nouveau: 'Nouveau',
    sans_reponse_1: 'Sans réponse 1',
    sans_reponse_2: 'Sans réponse 2',
    sans_reponse_3: 'Sans réponse 3',
    a_rappeler: 'À rappeler',
    devis_estimatif: 'Devis estimatif',
    listing_en_attente: 'Listing en attente',
    listing_telephone: 'Listing par téléphone',
    listing_email: 'Listing par email',
    visite: 'Visite',
    faux_contact: 'Faux contact',
    non_interessant: 'Non intéressant',
};
const VISITE_STATUS_COLORS = {
    a_venir: 'bg-blue-100 text-blue-700 border-blue-200',
    commencee: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    decalee: 'bg-orange-100 text-orange-700 border-orange-200',
    annulee: 'bg-red-100 text-red-700 border-red-200',
    terminee: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};
const VISITE_STATUS_LABELS = {
    a_venir: 'À venir',
    commencee: 'Commencée',
    decalee: 'À reprogrammer',
    annulee: 'Annulée',
    terminee: 'Terminée',
};
const INVENTAIRE_TYPE_LABELS = {
    visite_virtuelle: 'Visite virtuelle',
    visite_presentielle: 'Visite présentielle',
    listing_telephone: 'Listing téléphone',
    listing_email: 'Listing email',
    devis_estimatif: 'Devis estimatif',
};
const INVENTAIRE_STATUS_LABELS = {
    nouveau: 'Nouveau',
    en_cours: 'En cours',
    termine: 'Terminé',
    valide: 'Validé',
    devis_envoye: 'Devis envoyé',
    accepte: 'Accepté',
    refuse: 'Refusé',
};
const INVENTAIRE_STATUS_COLORS = {
    nouveau: 'bg-blue-100 text-blue-700 border-blue-200',
    en_cours: 'bg-amber-100 text-amber-700 border-amber-200',
    termine: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    valide: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    devis_envoye: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    accepte: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    refuse: 'bg-red-100 text-red-700 border-red-200',
};
const DEVIS_STATUS_COLORS = {
    brouillon: 'bg-slate-100 text-slate-700 border-slate-200',
    en_attente: 'bg-amber-100 text-amber-700 border-amber-200',
    accepte: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    refuse: 'bg-red-100 text-red-700 border-red-200',
};
const DEVIS_STATUTS_FULL = {
    a_envoyer: { label: 'À envoyer', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    envoye: { label: 'Envoyé', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    relance_1: { label: 'Relancé 1', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    relance_2: { label: 'Relancé 2', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    relance_3: { label: 'Relancé 3', color: 'bg-red-100 text-red-700 border-red-200' },
    interesse: { label: 'Intéressé', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    accepte: { label: 'Accepté', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    refuse: { label: 'Refusé', color: 'bg-red-100 text-red-700 border-red-200' },
    sans_reponse: { label: 'Sans réponse', color: 'bg-slate-100 text-slate-600 border-slate-200' },
};
const FACTURE_STATUS_COLORS = {
    brouillon: 'bg-slate-100 text-slate-700 border-slate-200',
    emise: 'bg-blue-100 text-blue-700 border-blue-200',
    payee_partiellement: 'bg-amber-100 text-amber-700 border-amber-200',
    payee: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    annulee: 'bg-red-100 text-red-700 border-red-200',
};
const STATUT_GENERIC_COLORS = {
    a_demander: 'bg-amber-100 text-amber-700 border-amber-200',
    en_cours: 'bg-blue-100 text-blue-700 border-blue-200',
    obtenue: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    a_reserver: 'bg-amber-100 text-amber-700 border-amber-200',
    reservee: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    a_prevoir: 'bg-amber-100 text-amber-700 border-amber-200',
    actif: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    sortie: 'bg-slate-100 text-slate-700 border-slate-200',
    a_traiter: 'bg-amber-100 text-amber-700 border-amber-200',
    resolu: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

// AOT — labels & couleurs identiques à AotPage
const AOT_STATUTS = {
    a_demander: { label: 'A demander', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    hors_delais: { label: 'Hors delais', color: 'bg-red-100 text-red-700 border-red-200' },
    en_cours: { label: 'En cours', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    accepte: { label: 'Accepte', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    refuse: { label: 'Refuse', color: 'bg-red-100 text-red-700 border-red-200' },
};
const MM_STATUTS = {
    a_reserver: { label: 'A reserver', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    reserve: { label: 'Reserve', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    termine: { label: 'Termine', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};
const GM_STATUTS = {
    a_prevoir: { label: 'A prevoir', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    reserve: { label: 'Reservé', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    actif: { label: 'Actif', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    termine: { label: 'Terminé', color: 'bg-slate-100 text-slate-600 border-slate-200' },
};
const PROB_STATUTS = {
    a_traiter: { label: 'À traiter', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    en_cours: { label: 'En cours', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    resolu: { label: 'Résolu', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const MODULE_META = {
    prospects: { icon: User, color: 'text-[#2e7cd6]', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Liste des prospects' },
    visites: { icon: Calendar, color: 'text-[#2e7cd6]', bg: 'bg-cyan-50', border: 'border-cyan-200', label: 'Liste des visites' },
    inventaires: { icon: Package, color: 'text-[#2e7cd6]', bg: 'bg-indigo-50', border: 'border-indigo-200', label: 'Liste des inventaires' },
    devis: { icon: FileText, color: 'text-[#2e7cd6]', bg: 'bg-violet-50', border: 'border-violet-200', label: 'Liste des devis' },
    clients: { icon: UserCheck, color: 'text-[#2e7cd6]', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Liste des clients' },
    aot: { icon: ShieldCheck, color: 'text-[#2e7cd6]', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Liste des AOT' },
    monte_meuble: { icon: Truck, color: 'text-[#2e7cd6]', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Liste des monte-meubles' },
    garde_meuble: { icon: Warehouse, color: 'text-[#2e7cd6]', bg: 'bg-purple-50', border: 'border-purple-200', label: 'Liste des garde-meubles' },
    factures: { icon: Receipt, color: 'text-[#2e7cd6]', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Liste des factures' },
    pointage: { icon: Clock, color: 'text-[#2e7cd6]', bg: 'bg-violet-50', border: 'border-violet-200', label: 'Liste des pointages déménageurs' },
    problemes: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Problèmes à régler' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDateFr(iso) {
    if (!iso) return '-';
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return format(d, 'dd/MM/yyyy', { locale: fr });
    } catch { return iso; }
}
function formatDateTimeFr(iso) {
    if (!iso) return '-';
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return format(d, 'dd/MM/yyyy HH:mm', { locale: fr });
    } catch { return iso; }
}
function timeAgo(iso) {
    if (!iso) return '-';
    try {
        const diff = Date.now() - new Date(iso).getTime();
        const m = Math.floor(diff / 60000);
        if (m < 1) return "à l'instant";
        if (m < 60) return `il y a ${m} min`;
        const h = Math.floor(m / 60);
        if (h < 24) return `il y a ${h} h`;
        const d = Math.floor(h / 24);
        if (d < 30) return `il y a ${d} j`;
        return formatDateFr(iso);
    } catch { return iso; }
}
function formatEuro(n) {
    if (n === null || n === undefined) return '';
    try {
        return Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
    } catch { return `${n} €`; }
}
function formatEuroFact(n) {
    if (n === null || n === undefined) return '0.00 EUR';
    try {
        return Number(n).toFixed(2) + ' EUR';
    } catch { return `${n} EUR`; }
}
function daysUntil(dateStr) {
    if (!dateStr) return null;
    try {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const d = new Date(dateStr);
        d.setHours(0, 0, 0, 0);
        return Math.round((d - now) / (1000 * 60 * 60 * 24));
    } catch { return null; }
}

// ---------------------------------------------------------------------------
// Composant principal
// ---------------------------------------------------------------------------
export function MaRechercheSection({ autoFocus = false }) {
    const navigate = useNavigate();
    const [q, setQ] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSugg, setShowSugg] = useState(false);
    const [loadingSugg, setLoadingSugg] = useState(false);
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState(null);
    const [prospectConfig, setProspectConfig] = useState(null);
    const [historyProspect, setHistoryProspect] = useState(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [historyVisite, setHistoryVisite] = useState(null);
    const [isVisiteHistoryOpen, setIsVisiteHistoryOpen] = useState(false);
    const debounceRef = useRef(null);
    const wrapRef = useRef(null);

    // Récupère la config Prospects (sources/statuts) une seule fois pour mapper les libellés
    useEffect(() => {
        axios.get(`${API}/api/prospects/config`, { withCredentials: true })
            .then(({ data }) => setProspectConfig(data))
            .catch(() => setProspectConfig(null));
    }, []);

    const getSourceLabel = (source) => prospectConfig?.sources?.find(s => s.value === source)?.label || source || '-';

    const fetchSuggestions = useCallback(async (term) => {
        if (!term || term.length < 2) { setSuggestions([]); return; }
        setLoadingSugg(true);
        try {
            const { data } = await axios.get(`${API}/api/recherche/suggestions`, {
                params: { q: term }, withCredentials: true,
            });
            setSuggestions(data.suggestions || []);
        } catch { setSuggestions([]); }
        finally { setLoadingSugg(false); }
    }, []);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchSuggestions(q), 250);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [q, fetchSuggestions]);

    useEffect(() => {
        const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowSugg(false); };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const runSearch = async (term) => {
        const v = (term ?? q).trim();
        if (v.length < 2) return;
        setSearching(true); setShowSugg(false);
        try {
            const { data } = await axios.get(`${API}/api/recherche/global`, {
                params: { q: v }, withCredentials: true,
            });
            setResults(data);
        } catch { setResults({ q: v, total: 0, results: {} }); }
        finally { setSearching(false); }
    };

    const onSelectSuggestion = (s) => { setQ(s.value); runSearch(s.value); };
    const reset = () => { setQ(''); setResults(null); setSuggestions([]); };

    // -----------------------------------------------------------------
    // Tables — un rendu par module (fidèle aux pages d'origine)
    // -----------------------------------------------------------------
    const renderProspects = (items) => (
        <Table className="min-w-[700px]">
            <TableHeader>
                <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Date de création</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Modifié</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {items.map((p) => (
                    <TableRow
                        key={p.id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => navigate(`/prospects?focus=${p.id}`)}
                        data-testid={`recherche-prospect-${p.id}`}
                    >
                        <TableCell>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                    <User size={20} className="text-slate-500" />
                                </div>
                                <div>
                                    <p className="font-medium text-slate-900">{p.client_nom}</p>
                                </div>
                            </div>
                        </TableCell>
                        <TableCell className="w-10">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setHistoryProspect(p);
                                    setIsHistoryOpen(true);
                                }}
                                className="p-1 rounded-md hover:bg-blue-50 transition-colors"
                                title="Historique des statuts"
                                data-testid={`recherche-prospect-history-${p.id}`}
                            >
                                <Star size={18} className="text-[#2e7cd6] fill-[#2e7cd6]" />
                            </button>
                        </TableCell>
                        <TableCell className="text-slate-600">{formatDateFr(p.created_at)}</TableCell>
                        <TableCell className="text-slate-600">{getSourceLabel(p.source)}</TableCell>
                        <TableCell>
                            <Badge
                                variant="outline"
                                className={`${PROSPECT_STATUS_COLORS[p.statut] || 'bg-slate-100 text-slate-700'} border`}
                            >
                                {PROSPECT_STATUS_LABELS[p.statut] || p.statut}
                            </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                            {timeAgo(p.status_changed_at || p.updated_at || p.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => { e.stopPropagation(); navigate(`/prospects?focus=${p.id}`); }}
                                    title="Voir"
                                    data-testid={`recherche-prospect-view-${p.id}`}
                                >
                                    <Eye size={16} />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => { e.stopPropagation(); navigate(`/prospects?focus=${p.id}&action=delete`); }}
                                    title="Supprimer"
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                    data-testid={`recherche-prospect-delete-${p.id}`}
                                >
                                    <Trash2 size={16} />
                                </Button>
                            </div>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    const renderVisites = (items) => (
        <Table className="min-w-[820px]">
            <TableHeader>
                <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Commercial</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Heure</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {items.map((v) => (
                    <TableRow
                        key={v.id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => navigate('/visites-liste')}
                        data-testid={`recherche-visite-${v.id}`}
                    >
                        <TableCell>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-cyan-50 flex items-center justify-center">
                                    <User size={20} className="text-cyan-600" />
                                </div>
                                <div>
                                    <p className="font-medium text-slate-900">{v.client_nom || '—'}</p>
                                    {v.telephone && <p className="text-xs text-slate-400">{v.telephone}</p>}
                                </div>
                            </div>
                        </TableCell>
                        <TableCell className="w-10">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setHistoryVisite({ id: v.id, prospect_nom: v.client_nom });
                                    setIsVisiteHistoryOpen(true);
                                }}
                                className="p-1 rounded-md hover:bg-blue-50 transition-colors"
                                title="Historique de la visite"
                                data-testid={`recherche-visite-history-${v.id}`}
                            >
                                <Star size={18} className="text-[#2e7cd6] fill-[#2e7cd6]" />
                            </button>
                        </TableCell>
                        <TableCell>
                            <Badge
                                variant="outline"
                                className={v.type_visite === 'virtuelle'
                                    ? 'bg-violet-100 text-violet-700 border-violet-200'
                                    : 'bg-cyan-100 text-cyan-700 border-cyan-200'}
                            >
                                {v.type_visite === 'virtuelle' ? (
                                    <span className="inline-flex items-center gap-1"><Monitor size={12} /> Virtuelle</span>
                                ) : (
                                    <span className="inline-flex items-center gap-1"><MapPin size={12} /> Présentielle</span>
                                )}
                            </Badge>
                        </TableCell>
                        <TableCell>
                            {v.commercial_nom ? (
                                <Badge
                                    variant="outline"
                                    className="border text-xs"
                                    style={v.commercial_couleur ? {
                                        backgroundColor: v.commercial_couleur + '1A',
                                        color: v.commercial_couleur,
                                        borderColor: v.commercial_couleur + '40',
                                    } : {}}
                                >
                                    {v.commercial_nom}
                                </Badge>
                            ) : (
                                <span className="text-slate-400 text-xs italic">Non affecté</span>
                            )}
                        </TableCell>
                        <TableCell className="text-slate-600">
                            <span className="inline-flex items-center gap-1">
                                <CalendarDays size={13} className="text-slate-400" />
                                {formatDateFr(v.date)}
                            </span>
                        </TableCell>
                        <TableCell className="text-slate-600">
                            <span className="inline-flex items-center gap-1">
                                <Clock size={13} className="text-slate-400" />
                                {v.heure || '-'}
                            </span>
                        </TableCell>
                        <TableCell>
                            <Badge
                                variant="outline"
                                className={`${VISITE_STATUS_COLORS[v.statut] || 'bg-slate-100 text-slate-700'} border`}
                            >
                                {VISITE_STATUS_LABELS[v.statut] || v.statut}
                            </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => { e.stopPropagation(); navigate('/visites-liste'); }}
                                title="Voir"
                                data-testid={`recherche-visite-view-${v.id}`}
                            >
                                <Eye size={16} />
                            </Button>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    const renderInventaires = (items) => (
        <Table className="min-w-[820px]">
            <TableHeader>
                <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Date de création</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Commercial</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Modifié</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {items.map((i) => (
                    <TableRow
                        key={i.id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => navigate(`/devis/${i.id}`)}
                        data-testid={`recherche-inv-${i.id}`}
                    >
                        <TableCell>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center">
                                    <User size={20} className="text-indigo-600" />
                                </div>
                                <div>
                                    <p className="font-medium text-slate-900">{i.client_nom || '—'}</p>
                                    {i.telephone && <p className="text-xs text-slate-400">{i.telephone}</p>}
                                </div>
                            </div>
                        </TableCell>
                        <TableCell className="text-slate-600">{formatDateFr(i.created_at)}</TableCell>
                        <TableCell>
                            <Badge variant="outline" className="bg-violet-100 text-violet-700 border-violet-200">
                                {INVENTAIRE_TYPE_LABELS[i.type_inventaire] || (i.type_inventaire || '-').replace(/_/g, ' ')}
                            </Badge>
                        </TableCell>
                        <TableCell>
                            {i.commercial_nom ? (
                                <Badge
                                    variant="outline"
                                    className="border text-xs"
                                    style={i.commercial_couleur ? {
                                        backgroundColor: i.commercial_couleur + '1A',
                                        color: i.commercial_couleur,
                                        borderColor: i.commercial_couleur + '40',
                                    } : {}}
                                >
                                    {i.commercial_nom}
                                </Badge>
                            ) : (
                                <span className="text-slate-400 text-xs italic">-</span>
                            )}
                        </TableCell>
                        <TableCell>
                            <Badge
                                variant="outline"
                                className={`${INVENTAIRE_STATUS_COLORS[i.statut] || 'bg-slate-100 text-slate-700'} border`}
                            >
                                {INVENTAIRE_STATUS_LABELS[i.statut] || (i.statut || '-').replace(/_/g, ' ')}
                            </Badge>
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                            {timeAgo(i.updated_at || i.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => { e.stopPropagation(); navigate(`/devis/${i.id}`); }}
                                    title="Voir"
                                    data-testid={`recherche-inv-view-${i.id}`}
                                >
                                    <Eye size={16} />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => { e.stopPropagation(); navigate(`/inventaires?delete=${i.id}`); }}
                                    title="Supprimer"
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                    data-testid={`recherche-inv-delete-${i.id}`}
                                >
                                    <Trash2 size={16} />
                                </Button>
                            </div>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    const renderDevis = (items) => (
        <Table className="min-w-[700px]">
            <TableHeader>
                <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Commercial</TableHead>
                    <TableHead>Jours restants</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Modifié</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {items.map((d) => {
                    const jours = daysUntil(d.date_demenagement);
                    // Pick the most relevant statut (même logique que DevisPage)
                    const statuts = d.statuts || [];
                    const mainStatut = statuts.includes('accepte') ? 'accepte'
                        : statuts.includes('envoye') ? 'envoye'
                        : statuts.includes('relance_1') ? 'relance_1'
                        : statuts.includes('relance_2') ? 'relance_2'
                        : statuts.includes('relance_3') ? 'relance_3'
                        : statuts.includes('refuse') ? 'refuse'
                        : statuts.includes('sans_reponse') ? 'sans_reponse'
                        : statuts[0] || 'a_envoyer';
                    const statutInfo = DEVIS_STATUTS_FULL[mainStatut] || DEVIS_STATUTS_FULL.a_envoyer;
                    return (
                        <TableRow
                            key={d.id}
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => d.inventaire_id ? navigate(`/devis/${d.inventaire_id}`) : navigate('/devis')}
                            data-testid={`recherche-devis-${d.id}`}
                        >
                            <TableCell>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                        <User size={20} className="text-slate-500" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900">{d.client_nom || 'Sans nom'}</p>
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell className="w-10">
                                <Star size={18} className="text-[#2e7cd6] fill-[#2e7cd6]" />
                            </TableCell>
                            <TableCell>
                                {d.commercial_nom ? (
                                    <div className="flex items-center gap-2">
                                        {d.commercial_couleur && (
                                            <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: d.commercial_couleur }} />
                                        )}
                                        <span className="text-slate-600">{d.commercial_nom}</span>
                                    </div>
                                ) : (
                                    <span className="text-slate-400 italic">Non assigné</span>
                                )}
                            </TableCell>
                            <TableCell>
                                {jours !== null ? (
                                    <Badge
                                        variant="outline"
                                        className={`border ${
                                            jours < 0 ? 'bg-red-100 text-red-600 border-red-200' :
                                            jours <= 7 ? 'bg-orange-100 text-orange-600 border-orange-200' :
                                            jours <= 30 ? 'bg-amber-100 text-amber-600 border-amber-200' :
                                            'bg-slate-100 text-slate-600 border-slate-200'
                                        }`}
                                    >
                                        {jours < 0 ? `${Math.abs(jours)}j dépassé` : `${jours}j`}
                                    </Badge>
                                ) : (
                                    <span className="text-slate-400">-</span>
                                )}
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline" className={`${statutInfo.color} border`}>
                                    {statutInfo.label}
                                </Badge>
                            </TableCell>
                            <TableCell className="text-slate-500 text-sm">
                                {timeAgo(d.updated_at)}
                            </TableCell>
                            <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={(e) => { e.stopPropagation(); navigate(`/devis/${d.inventaire_id}`); }}
                                        title="Voir"
                                        data-testid={`recherche-devis-view-${d.id}`}
                                    >
                                        <Eye size={16} />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={(e) => { e.stopPropagation(); navigate(`/devis`); }}
                                        title="Supprimer"
                                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                        data-testid={`recherche-devis-delete-${d.id}`}
                                    >
                                        <Trash2 size={16} />
                                    </Button>
                                </div>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );

    const isAotParis = (adresse) => {
        if (!adresse) return false;
        const s = adresse.toLowerCase();
        return s.includes('paris') || /\b75\d{3}\b/.test(s);
    };
    const isAotUrgent = (a) => {
        if (!a.date_demenagement) return false;
        const j = daysUntil(a.date_demenagement);
        if (j === null || j < 0) return false;
        if (a.statut === 'a_demander') {
            const seuil = isAotParis(a.adresse) ? 12 : 25;
            return j <= seuil;
        }
        if (a.statut === 'en_cours' && j <= 5) return true;
        return false;
    };

    const renderAot = (items) => (
        <Table className="min-w-[700px]">
            <TableHeader>
                <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Adresse</TableHead>
                    <TableHead>Date demenagement</TableHead>
                    <TableHead>Statut</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {items.map((a) => {
                    const urgent = isAotUrgent(a);
                    return (
                        <TableRow
                            key={a.id}
                            className={`cursor-pointer hover:bg-slate-50 ${urgent ? 'bg-red-50/50' : ''}`}
                            onClick={() => navigate('/aot')}
                            data-testid={`recherche-aot-${a.id}`}
                        >
                            <TableCell>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                                        <ParkingCircle size={20} className="text-[#2e7cd6]" />
                                    </div>
                                    <div className="flex items-center gap-2 min-w-0">
                                        <p className="font-medium text-slate-900 truncate">{a.client_nom}</p>
                                        {urgent && (
                                            <Badge className="bg-red-500 text-white border-0 text-[10px] px-1.5 py-0 h-5 animate-pulse">
                                                URGENT
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline" className={a.type === 'depart' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}>
                                    {a.type === 'depart' ? 'Depart' : 'Arrivee'}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <div className="flex items-start gap-1.5">
                                    <MapPin size={12} className={a.type === 'depart' ? 'text-emerald-500 mt-0.5' : 'text-red-500 mt-0.5'} />
                                    <span className="text-sm text-slate-600 line-clamp-1">{a.adresse || '-'}</span>
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center gap-1.5">
                                    <Calendar size={12} className="text-slate-400" />
                                    <span className={`text-sm ${a.is_programme ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                                        {a.is_programme ? formatDateFr(a.date_demenagement) : 'Non programmé'}
                                    </span>
                                </div>
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline" className={`text-xs border ${(AOT_STATUTS[a.statut] || AOT_STATUTS.a_demander).color}`}>
                                    {(AOT_STATUTS[a.statut] || AOT_STATUTS.a_demander).label}
                                </Badge>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );

    const isMmUrgent = (m) => {
        if (m.statut !== 'a_reserver' || !m.date_demenagement) return false;
        const j = daysUntil(m.date_demenagement);
        return j !== null && j >= 0 && j <= 5;
    };

    const renderMonteMeuble = (items) => (
        <Table className="min-w-[700px]">
            <TableHeader>
                <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Date demenagement</TableHead>
                    <TableHead>Adresse</TableHead>
                    <TableHead>Statut</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {items.map((m) => {
                    const urgent = isMmUrgent(m);
                    return (
                        <TableRow
                            key={m.id}
                            className={`cursor-pointer hover:bg-slate-50 ${urgent ? 'bg-red-50/50' : ''}`}
                            onClick={() => navigate('/monte-meuble')}
                            data-testid={`recherche-mm-${m.id}`}
                        >
                            <TableCell>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
                                        <Truck size={20} className="text-amber-600" />
                                    </div>
                                    <div className="flex items-center gap-2 min-w-0">
                                        <p className="font-medium text-slate-900 truncate">{m.client_nom}</p>
                                        {urgent && (
                                            <Badge className="bg-red-500 text-white border-0 text-[10px] px-1.5 py-0 h-5 animate-pulse">
                                                URGENT
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center gap-1.5">
                                    <Calendar size={12} className="text-slate-400" />
                                    <span className={`text-sm ${m.is_programme ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                                        {m.is_programme ? formatDateFr(m.date_demenagement) : 'Non programmé'}
                                    </span>
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className="flex items-start gap-1.5">
                                    <MapPin size={12} className={m.type === 'depart' ? 'text-emerald-500 mt-0.5' : 'text-red-500 mt-0.5'} />
                                    <div className="flex flex-col">
                                        <span className="text-sm text-slate-600 line-clamp-1">{m.adresse || '-'}</span>
                                        <Badge variant="outline" className={`text-xs mt-0.5 self-start ${m.type === 'depart' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                                            {m.type === 'depart' ? 'Depart' : 'Arrivee'}
                                        </Badge>
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline" className={`text-xs border ${(MM_STATUTS[m.statut] || MM_STATUTS.a_reserver).color}`}>
                                    {(MM_STATUTS[m.statut] || MM_STATUTS.a_reserver).label}
                                </Badge>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );

    const renderGardeMeuble = (items) => (
        <Table className="min-w-[700px]">
            <TableHeader>
                <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Date d'entree</TableHead>
                    <TableHead>Tarif mensuel</TableHead>
                    <TableHead>Duree</TableHead>
                    <TableHead>Date de sortie</TableHead>
                    <TableHead>Statut</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {items.map((g) => (
                    <TableRow key={g.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate('/garde-meuble')} data-testid={`recherche-gm-${g.id}`}>
                        <TableCell>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center shrink-0">
                                    <Warehouse size={20} className="text-purple-600" />
                                </div>
                                <p className="font-medium text-slate-900 truncate">{g.client_nom}</p>
                            </div>
                        </TableCell>
                        <TableCell>
                            <div className="flex items-center gap-1.5">
                                <Calendar size={12} className="text-slate-400" />
                                <span className={`text-sm ${g.is_programme ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                                    {g.is_programme ? formatDateFr(g.date_entree) : 'Non programmé'}
                                </span>
                            </div>
                        </TableCell>
                        <TableCell><span className="text-sm text-slate-700">{g.garde_meuble_tarif || '-'}</span></TableCell>
                        <TableCell><span className="text-sm text-slate-700">{g.garde_meuble_duree || '-'}</span></TableCell>
                        <TableCell>
                            <input
                                type="date"
                                min={g.date_entree || undefined}
                                defaultValue={g.date_sortie || ''}
                                onClick={(e) => e.stopPropagation()}
                                onChange={async (e) => {
                                    e.stopPropagation();
                                    const newDate = e.target.value;
                                    if (!newDate) return;
                                    try {
                                        await axios.patch(
                                            `${API}/api/garde-meuble/${g.id}/date-sortie`,
                                            { date_sortie: newDate },
                                            { withCredentials: true },
                                        );
                                    } catch { /* noop */ }
                                }}
                                className="border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#2e7cd6] w-[140px]"
                                data-testid={`recherche-gm-date-sortie-${g.id}`}
                            />
                        </TableCell>
                        <TableCell>
                            <Badge variant="outline" className={`text-xs border ${(GM_STATUTS[g.statut] || GM_STATUTS.a_prevoir).color}`}>
                                {(GM_STATUTS[g.statut] || GM_STATUTS.a_prevoir).label}
                            </Badge>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    const FACT_STATUT_ENVOI = {
        a_envoyer: { label: 'A envoyer', color: 'bg-red-100 text-red-700 border-red-200' },
        envoyee: { label: 'Envoyee', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    };
    const FACT_STATUT_PAIEMENT = {
        impayee: { label: 'Impayee', color: 'bg-red-100 text-red-700 border-red-200' },
        partiellement_payee: { label: 'Partiellement payee', color: 'bg-amber-100 text-amber-700 border-amber-200' },
        payee: { label: 'Payee', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    };
    const isFactureUrgent = (f) => {
        if (!f.date_demenagement) return false;
        const j = daysUntil(f.date_demenagement);
        if (j === null) return false;
        if (f.statut_paiement === 'impayee' && j >= 0 && j <= 5) return true;
        if (f.statut_paiement !== 'payee' && j < 0 && j >= -2) return true;
        return false;
    };

    const renderFactures = (items) => (
        <Table className="min-w-[820px]">
            <TableHeader>
                <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Date demenagement</TableHead>
                    <TableHead>N° Facture</TableHead>
                    <TableHead>Statut envoi</TableHead>
                    <TableHead>Prix TTC</TableHead>
                    <TableHead>Solde du</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {items.map((f) => {
                    const urgent = isFactureUrgent(f);
                    const env = FACT_STATUT_ENVOI[f.statut_envoi] || FACT_STATUT_ENVOI.a_envoyer;
                    const pai = FACT_STATUT_PAIEMENT[f.statut_paiement] || FACT_STATUT_PAIEMENT.impayee;
                    return (
                        <TableRow
                            key={f.id}
                            className={`cursor-pointer hover:bg-slate-50 ${urgent ? 'bg-red-50/50' : ''}`}
                            onClick={() => navigate(`/facturation/${f.id}`)}
                            data-testid={`recherche-fact-${f.id}`}
                        >
                            <TableCell>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                                        <Receipt size={20} className="text-emerald-600" />
                                    </div>
                                    <div className="flex items-center gap-2 min-w-0">
                                        <p className="font-medium text-slate-900 truncate">{f.client_nom}</p>
                                        {urgent && (
                                            <Badge className="bg-red-500 text-white border-0 text-[10px] px-1.5 py-0 h-5 animate-pulse">
                                                URGENT
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center gap-1.5">
                                    <Calendar size={12} className="text-slate-400" />
                                    <span className={`text-sm ${f.is_programme ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                                        {f.is_programme ? formatDateFr(f.date_demenagement) : 'Non programmé'}
                                    </span>
                                </div>
                            </TableCell>
                            <TableCell className="font-mono text-sm text-slate-800">{f.numero_facture || '—'}</TableCell>
                            <TableCell>
                                <Badge variant="outline" className={`${env.color} border`}>{env.label}</Badge>
                            </TableCell>
                            <TableCell className="text-slate-700">{formatEuroFact(f.total_ttc)}</TableCell>
                            <TableCell className="text-slate-700">{formatEuroFact(f.solde_du ?? f.total_ttc)}</TableCell>
                            <TableCell>
                                <Badge variant="outline" className={`${pai.color} border`}>{pai.label}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={(e) => { e.stopPropagation(); navigate(`/facturation/${f.id}`); }}
                                        title="Voir"
                                        data-testid={`recherche-fact-view-${f.id}`}
                                    >
                                        <Eye size={16} />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={(e) => { e.stopPropagation(); navigate(`/facturation`); }}
                                        title="Supprimer"
                                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                        data-testid={`recherche-fact-delete-${f.id}`}
                                    >
                                        <Trash2 size={16} />
                                    </Button>
                                </div>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );

    const renderPointage = (items) => (
        <Table className="min-w-[820px]">
            <TableHeader>
                <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Ville</TableHead>
                    <TableHead>Déménageurs pointés</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {items.map((t) => (
                    <TableRow
                        key={t.id}
                        className={`cursor-pointer transition-colors ${t.pointages_count === 0 ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-50'}`}
                        onClick={() => navigate('/exploitation/pointage-demenageurs')}
                        data-testid={`recherche-pt-${t.id}`}
                    >
                        <TableCell className="font-medium text-slate-900 whitespace-nowrap">
                            <span className="inline-flex items-center gap-2">
                                {t.event_type === 'livraison' ? (
                                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-50 text-amber-500" title="Livraison">
                                        <Package size={16} />
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-[#2e7cd6]" title="Déménagement">
                                        <Truck size={16} />
                                    </span>
                                )}
                                {t.client_nom || '-'}
                            </span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-slate-600 text-sm">
                            <span className="inline-flex items-center gap-1.5">
                                <Calendar size={13} className="text-slate-400" />
                                {formatDateFr(t.date)}
                            </span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                            <Badge variant="outline" className="text-xs bg-white text-slate-700 border-emerald-300">
                                {t.type_prestation || '-'}
                            </Badge>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-slate-600 text-sm">
                            {t.ville
                                ? <span className="inline-flex items-center gap-1"><MapPin size={12} className="text-slate-400" />{t.ville}</span>
                                : <span className="text-slate-400">-</span>}
                        </TableCell>
                        <TableCell>
                            {t.pointages_count === 0
                                ? <span className="text-slate-400 text-xs italic">Aucun pointage</span>
                                : (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-medium">
                                        <CheckCircle2 size={12} />
                                        {t.pointages_count} déménageur{t.pointages_count > 1 ? 's' : ''}
                                    </span>
                                )}
                        </TableCell>
                        <TableCell className="text-right">
                            <Button
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); navigate('/exploitation/pointage-demenageurs'); }}
                                className="bg-[#2e7cd6] hover:bg-[#256bb5] text-white"
                                data-testid={`recherche-pt-action-${t.id}`}
                            >
                                <span className="mr-1">+</span> Pointage
                            </Button>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    const renderProblemes = (items) => (
        <Table className="min-w-[800px]">
            <TableHeader>
                <TableRow>
                    <TableHead className="w-[160px]">Client</TableHead>
                    <TableHead className="w-[100px]">Date</TableHead>
                    <TableHead className="w-[80px]">Description</TableHead>
                    <TableHead className="w-[100px]">PDF</TableHead>
                    <TableHead className="w-[120px]">Coût</TableHead>
                    <TableHead className="w-[140px]">Statut</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {items.map((p) => (
                    <TableRow key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate('/problemes')} data-testid={`recherche-prob-${p.id}`}>
                        <TableCell>
                            <div>
                                <p className="font-medium text-slate-900 text-sm">{p.client_nom || '-'}</p>
                                <div className="flex flex-col gap-0.5 mt-1">
                                    {p.client_telephone && (
                                        <span className="flex items-center gap-1 text-[11px] text-slate-500">
                                            <Phone size={10} className="text-slate-400" />{p.client_telephone}
                                        </span>
                                    )}
                                    {p.client_email && (
                                        <span className="flex items-center gap-1 text-[11px] text-slate-500">
                                            <Mail size={10} className="text-slate-400" />{p.client_email}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </TableCell>
                        <TableCell><span className="text-xs text-slate-500">{formatDateFr(p.created_at)}</span></TableCell>
                        <TableCell>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        onClick={(e) => e.stopPropagation()}
                                        className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-slate-200 bg-slate-50 text-slate-500 hover:bg-blue-50 hover:text-[#2e7cd6] hover:border-[#2e7cd6] transition-colors"
                                        title="Voir la description"
                                        data-testid={`recherche-prob-desc-view-${p.id}`}
                                    >
                                        <Eye size={14} />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[320px] text-xs whitespace-pre-wrap break-words" align="start" onClick={(e) => e.stopPropagation()}>
                                    <p className="font-semibold text-slate-700 mb-1.5 text-[11px] uppercase tracking-wide">Description</p>
                                    <p className="text-slate-600 leading-relaxed">{p.description || <span className="italic text-slate-400">Pas de description</span>}</p>
                                </PopoverContent>
                            </Popover>
                        </TableCell>
                        <TableCell>
                            <div className="flex items-center gap-1 flex-wrap">
                                {(p.dossiers || []).map((d, i) => (
                                    <FileText key={i} size={20} className="text-red-500" />
                                ))}
                                {(!p.dossiers || p.dossiers.length === 0) && (
                                    <span className="text-xs text-slate-300">-</span>
                                )}
                            </div>
                        </TableCell>
                        <TableCell><span className="text-sm font-medium text-slate-800">{p.cout || '-'}</span></TableCell>
                        <TableCell>
                            <Badge variant="outline" className={`text-xs border ${(PROB_STATUTS[p.statut] || PROB_STATUTS.a_traiter).color}`}>
                                {(PROB_STATUTS[p.statut] || PROB_STATUTS.a_traiter).label}
                            </Badge>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    const CLIENT_LIVRAISON_LABELS = {
        sans_livraison: { label: 'Sans livraison', color: 'bg-slate-100 text-slate-600 border-slate-200' },
        a_programmer: { label: 'A programmer', color: 'bg-amber-100 text-amber-700 border-amber-200' },
        deja_programmee: { label: 'Deja programmee', color: 'bg-blue-100 text-blue-700 border-blue-200' },
        termine: { label: 'Termine', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    };
    const CLIENT_ACOMPTE_LABELS = {
        a_regle: { label: 'A regler', color: 'bg-amber-100 text-amber-700 border-amber-200' },
        effectue: { label: 'Acompte regle', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    };
    const CLIENT_STATUT_LABELS = {
        a_verifier: { label: 'A verifier', color: 'bg-amber-100 text-amber-700 border-amber-200' },
        pret: { label: 'Pret', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
        annule: { label: 'Annule', color: 'bg-red-100 text-red-700 border-red-200' },
    };

    const renderClients = (items) => (
        <Table className="min-w-[820px]">
            <TableHeader>
                <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Date déménagement</TableHead>
                    <TableHead>Livraison</TableHead>
                    <TableHead>Acompte</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {items.map((c) => {
                    const liv = CLIENT_LIVRAISON_LABELS[c.statut_livraison] || CLIENT_LIVRAISON_LABELS.sans_livraison;
                    const aco = CLIENT_ACOMPTE_LABELS[c.acompte] || CLIENT_ACOMPTE_LABELS.a_regle;
                    const sta = CLIENT_STATUT_LABELS[c.statut_client] || CLIENT_STATUT_LABELS.a_verifier;
                    const isAnnule = c.statut_client === 'annule';
                    const programme = c.statut_prestation === 'programme' && c.date_demenagement;
                    return (
                        <TableRow
                            key={c.id}
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => navigate(`/clients/${c.id}`)}
                            data-testid={`recherche-client-${c.id}`}
                        >
                            <TableCell>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                                        <Truck size={20} className={isAnnule ? 'text-red-500' : 'text-[#2e7cd6]'} />
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900">{c.client_nom || '—'}</p>
                                        {c.commercial_nom && (
                                            <p className="text-xs text-slate-500 flex items-center gap-1.5">
                                                {c.commercial_couleur && (
                                                    <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: c.commercial_couleur }} />
                                                )}
                                                {c.commercial_nom}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                {programme ? (
                                    <div>
                                        <p className="text-sm text-slate-700">{formatDateFr(c.date_demenagement)}</p>
                                    </div>
                                ) : (
                                    <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">
                                        À programmer
                                    </Badge>
                                )}
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline" className={`${liv.color} border`}>{liv.label}</Badge>
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline" className={`${aco.color} border`}>{aco.label}</Badge>
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline" className={`${sta.color} border`}>{sta.label}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={(e) => { e.stopPropagation(); navigate(`/clients/${c.id}`); }}
                                        title="Voir"
                                        data-testid={`recherche-client-view-${c.id}`}
                                    >
                                        <Eye size={16} />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={(e) => { e.stopPropagation(); navigate(`/clients`); }}
                                        title="Supprimer"
                                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                        data-testid={`recherche-client-delete-${c.id}`}
                                    >
                                        <Trash2 size={16} />
                                    </Button>
                                </div>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );

    const RENDERERS = {
        prospects: renderProspects,
        visites: renderVisites,
        inventaires: renderInventaires,
        devis: renderDevis,
        clients: renderClients,
        aot: renderAot,
        monte_meuble: renderMonteMeuble,
        garde_meuble: renderGardeMeuble,
        factures: renderFactures,
        pointage: renderPointage,
        problemes: renderProblemes,
    };

    const renderModuleCard = (key, items) => {
        if (!items || items.length === 0) return null;
        const meta = MODULE_META[key];
        const Icon = meta.icon;
        const renderer = RENDERERS[key];
        return (
            <Card key={key} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`recherche-card-${key}`}>
                <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
                    <Icon size={20} className={meta.color} />
                    <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {meta.label} ({items.length})
                    </h3>
                </div>
                <div className="overflow-x-auto p-0 sm:p-6">
                    {renderer(items)}
                </div>
            </Card>
        );
    };

    const grandTotal = results?.total ?? 0;
    const moduleOrder = ['problemes', 'pointage', 'factures', 'garde_meuble', 'monte_meuble', 'aot', 'clients', 'devis', 'inventaires', 'visites', 'prospects'];

    return (
        <div className="mb-6 space-y-8" data-testid="ma-recherche-section">
            {/* Barre de recherche — carte séparée */}
            <Card className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-visible">
                <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                            <Search size={18} className="text-[#2e7cd6]" />
                        </div>
                        <h2 className="text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Ma recherche
                        </h2>
                    </div>

                    <div ref={wrapRef} className="relative">
                        <div className="relative">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                value={q}
                                onChange={(e) => { setQ(e.target.value); setShowSugg(true); }}
                                onFocus={() => setShowSugg(true)}
                                onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); if (e.key === 'Escape') setShowSugg(false); }}
                                placeholder="Rechercher par nom, téléphone ou n° de devis..."
                                className="pl-10 pr-10 h-11 text-sm"
                                autoFocus={autoFocus}
                                data-testid="ma-recherche-input"
                            />
                            {q && (
                                <button
                                    onClick={reset}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                                    data-testid="ma-recherche-clear"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        {showSugg && q.length >= 2 && (
                            <div className="absolute top-full mt-1 left-0 right-0 bg-white rounded-lg shadow-lg border border-slate-200 z-30 max-h-72 overflow-y-auto">
                                {loadingSugg ? (
                                    <div className="px-4 py-4 text-center text-xs text-slate-400">
                                        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Recherche...
                                    </div>
                                ) : suggestions.length === 0 ? (
                                    <div className="px-4 py-3 text-center text-xs text-slate-400">
                                        Aucune suggestion.&nbsp;
                                        <button onClick={() => runSearch()} className="text-[#2e7cd6] font-medium underline">Lancer la recherche</button>
                                    </div>
                                ) : (
                                    <ul>
                                        {suggestions.map((s, i) => (
                                            <li key={i}>
                                                <button
                                                    onClick={() => onSelectSuggestion(s)}
                                                    className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm"
                                                    data-testid={`ma-recherche-sugg-${i}`}
                                                >
                                                    {s.kind === 'telephone' && <Phone size={12} className="text-slate-400" />}
                                                    {s.kind === 'devis' && <FileText size={12} className="text-violet-500" />}
                                                    {s.kind === 'nom' && <User size={12} className="text-slate-400" />}
                                                    <span className="text-slate-700">{s.label}</span>
                                                    <span className="ml-auto text-[10px] text-slate-400 uppercase tracking-wide">{s.kind}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>

                    {results && !searching && (
                        <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                            <span>Résultats pour</span>
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">{results.q}</Badge>
                            <span>·</span>
                            <span className="font-semibold text-slate-700">{grandTotal} ligne{grandTotal > 1 ? 's' : ''}</span>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Loader / Résultats — chaque module dans sa propre carte */}
            {searching && (
                <div className="flex items-center justify-center py-6 text-slate-400 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Recherche en cours...
                </div>
            )}

            {results && !searching && grandTotal === 0 && (
                <Card className="bg-white rounded-2xl border border-slate-200 shadow-sm">
                    <CardContent className="text-center py-8 text-sm text-slate-400">
                        Aucun résultat trouvé pour cette recherche.
                    </CardContent>
                </Card>
            )}

            {results && !searching && grandTotal > 0 && (
                <div className="space-y-8">
                    {moduleOrder.map(k => renderModuleCard(k, results.results?.[k]))}
                </div>
            )}

            <StatusHistoryDialog
                open={isHistoryOpen}
                onOpenChange={(o) => { setIsHistoryOpen(o); if (!o) setHistoryProspect(null); }}
                prospect={historyProspect}
            />

            <VisiteHistoryDialog
                open={isVisiteHistoryOpen}
                onOpenChange={(o) => { setIsVisiteHistoryOpen(o); if (!o) setHistoryVisite(null); }}
                visite={historyVisite}
            />
        </div>
    );
}
